<template>
  <el-select
    :model-value="modelValue"
    :placeholder="placeholder"
    :clearable="clearable"
    :filterable="filterable"
    :disabled="disabled"
    :filter-method="filterMethod"
    @update:model-value="$emit('update:modelValue', $event)"
    @change="$emit('change', $event)"
    @clear="searchQuery = ''"
  >
    <template v-for="group in groupedAccounts" :key="group.type">
      <el-option-group :label="group.type">
        <el-option
          v-for="account in group.accounts"
          :key="account.id"
          :label="account.account_name"
          :value="account.id"
        >
          <div class="account-option">
            <span class="account-option-name">
              <span v-if="group.hasSubcats && account.account_subcategory" class="subcat-tag">{{ account.account_subcategory }}</span>
              {{ account.account_name }}
            </span>
            <span class="account-option-balance" :class="{ negative: parseFloat(account.balance) < 0 }">
              {{ formatBalance(account.balance) }}
            </span>
          </div>
        </el-option>
      </el-option-group>
    </template>
    <el-option v-if="noResults" disabled value="" label="无匹配账户" />
  </el-select>
</template>

<script>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

// 账户类型排序
const TYPE_ORDER = ['现金', '银行卡', '电子钱包', '投资账户', '借贷账户', '其他']

export default {
  name: 'AccountSelect',
  props: {
    modelValue: { type: Number, default: null },
    placeholder: { type: String, default: '选择账户' },
    clearable: { type: Boolean, default: true },
    filterable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    includeHidden: { type: Boolean, default: false },
    // 外部传入账户列表（可选，不传则用 store）
    accounts: { type: Array, default: null }
  },
  emits: ['update:modelValue', 'change'],
  setup(props) {
    const store = useStore()
    const searchQuery = ref('')

    const allAccounts = computed(() => {
      if (props.accounts !== null) return props.accounts
      // includeHidden 时使用包含隐藏账户的完整列表，避免编辑时账户显示为空
      const list = props.includeHidden
        ? (store.state.allAccounts.length ? store.state.allAccounts : store.state.accounts)
        : store.state.accounts
      return props.includeHidden ? list : list.filter(a => !a.is_hidden)
    })

    const filterMethod = (query) => {
      searchQuery.value = query.toLowerCase()
    }

    const visibleAccounts = computed(() => {
      if (!searchQuery.value) return allAccounts.value
      return allAccounts.value.filter(a =>
        a.account_name.toLowerCase().includes(searchQuery.value) ||
        (a.account_subcategory || '').toLowerCase().includes(searchQuery.value)
      )
    })

    const groupedAccounts = computed(() => {
      const map = {}
      for (const acc of visibleAccounts.value) {
        const type = acc.account_type || '其他'
        if (!map[type]) map[type] = []
        map[type].push(acc)
      }

      return TYPE_ORDER
        .filter(t => map[t])
        .concat(Object.keys(map).filter(t => !TYPE_ORDER.includes(t)))
        .map(type => {
          const accounts = map[type].slice().sort((a, b) => {
            const sa = a.account_subcategory || ''
            const sb = b.account_subcategory || ''
            return sa.localeCompare(sb) || a.account_name.localeCompare(b.account_name)
          })
          const subcats = new Set(accounts.map(a => a.account_subcategory).filter(Boolean))
          return { type, accounts, hasSubcats: subcats.size > 1 }
        })
    })

    const noResults = computed(() =>
      searchQuery.value && groupedAccounts.value.every(g => g.accounts.length === 0)
    )

    const formatBalance = (balance) => {
      const num = parseFloat(balance)
      if (isNaN(num)) return ''
      return (num >= 0 ? '¥' : '-¥') + Math.abs(num).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    return { searchQuery, groupedAccounts, noResults, filterMethod, formatBalance }
  }
}
</script>

<style scoped>
.account-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.account-option-name {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subcat-tag {
  font-size: 11px;
  color: #909399;
  background: #f4f4f5;
  padding: 0 4px;
  border-radius: 3px;
  flex-shrink: 0;
}

.account-option-balance {
  font-size: 12px;
  color: #67c23a;
  flex-shrink: 0;
}

.account-option-balance.negative {
  color: #f56c6c;
}
</style>
